import { MouseEvent, useMemo, useState } from "react";
import axios, { AxiosRequestConfig } from "axios";
import { Box, Checkbox, CircularProgress, Divider, ListItemText, Menu, MenuItem } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import moment from "moment";
import Layout from "../../Components/Layout/Layout";
import Tile from "../../Components/Tiles/Tile";
import TileContent from "../../Components/Tiles/TileContent";
import TileActions from "../../Components/Tiles/TileActions";
import ActionButton from "../../Components/ActionButton";
import Chip from "../../Components/Chip";
import useAxiosEffect from "../../system/useAxiosEffect";
import useChartOptions from "../../system/useChartOptions";
import useUser from "../../system/useUser";
import getInterval, { Interval } from "../../Components/getInterval";
import { CompoundManagementGroup } from "../../system/Domain";
import CompoundNewOrdersTile from "./CompoundNewOrdersTile";
import CompoundMissingPhotosTile from "./CompoundMissingPhotosTile";
import CompoundMissingFirstPhotosTile from "./CompoundMissingFirstPhotosTile";
import CompoundReturnsChart from "./CompoundReturnsChart";
import CompoundServiceChart from "./CompoundServiceChart";

// Dorfmark's id, used as the fallback default compound for users whose own company isn't one of
// the selectable compound-management companies (e.g. Dekra users, or users without a company).
const defaultCompoundId = "661d179b0000000000963043";

export default () => {
	const [user] = useUser();
	const [groups, setGroups] = useState<CompoundManagementGroup[]>();
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [openCompanyId, setOpenCompanyId] = useState<number | undefined>(undefined);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [options] = useChartOptions();
	const [interval, setInterval] = useState<Interval>({
		dateFrom: moment().startOf("month").format(moment.HTML5_FMT.DATE),
		dateTo: moment().endOf("month").format(moment.HTML5_FMT.DATE)
	});

	const loadGroups = async (axiosConfig: AxiosRequestConfig) => {
		const { data } = await axios.get<CompoundManagementGroup[]>(`/api/compounds/dashboard/groups`, axiosConfig);

		setGroups(data);

		// Default selection: the user's own company's compounds if their company is one of the
		// selectable groups, otherwise fall back to Dorfmark (or the first available compound, if
		// even Dorfmark isn't selectable for this user).
		const ownGroup = data.find((g) => g.company.id === user?.company?.id);
		const allCompounds = data.flatMap((g) => g.compounds);
		const fallbackCompound =
			allCompounds.find((c) => c.id === defaultCompoundId) ?? allCompounds[0];

		if (ownGroup) {
			setSelectedIds(new Set(ownGroup.compounds.map((c) => c.id)));
		} else if (fallbackCompound) {
			setSelectedIds(new Set([fallbackCompound.id]));
		}
	};

	useAxiosEffect(loadGroups, []);

	const openGroup = (event: MouseEvent<HTMLElement>, companyId: number) => {
		setAnchorEl(event.currentTarget);
		setOpenCompanyId(companyId);
	};

	const closeGroup = () => {
		setAnchorEl(null);
		setOpenCompanyId(undefined);
	};

	const toggleCompound = (id: string) => {
		setSelectedIds((current) => {
			const next = new Set(current);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const toggleGroup = (group: CompoundManagementGroup) => {
		const groupIds = group.compounds.map((c) => c.id);
		const allSelected = groupIds.every((id) => selectedIds.has(id));

		setSelectedIds((current) => {
			const next = new Set(current);
			groupIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
			return next;
		});
	};

	const selectedCompounds = useMemo(
		() => (groups ?? []).flatMap((g) => g.compounds).filter((c) => selectedIds.has(c.id)),
		[groups, selectedIds]
	);

	const selectedCompoundIds = useMemo(() => selectedCompounds.map((c) => c.id), [selectedCompounds]);

	const handleChangeInterval = async () => {
		const result = await getInterval("Zeitraum eingeben", interval);
		if (result) {
			setInterval(result);
		}
	};

	const groupLabel = (group: CompoundManagementGroup) => {
		const selectedInGroup = group.compounds.filter((c) => selectedIds.has(c.id));

		if (selectedInGroup.length === 0) {
			return group.company.name;
		}

		if (selectedInGroup.length === group.compounds.length) {
			return `${group.company.name} (alle)`;
		}

		if (selectedInGroup.length === 1) {
			return `${group.company.name}: ${selectedInGroup[0].name}`;
		}

		return `${group.company.name} (${selectedInGroup.length}/${group.compounds.length})`;
	};

	const openGroupData = groups?.find((g) => g.company.id === openCompanyId);

	return (
		<Layout title="Platzübersicht">
			<Box sx={{ display: "flex", gap: "30px", flexDirection: "column" }}>
				{!groups && (
					<Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
						<CircularProgress size={48} />
					</Box>
				)}

				<Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
					{groups?.map((group) => {
						const selectedInGroup = group.compounds.filter((c) => selectedIds.has(c.id));

						return (
							<Box
								key={group.company.id}
								onClick={(event: MouseEvent<HTMLDivElement>) => openGroup(event, group.company.id)}
								sx={{ display: "inline-flex" }}
							>
								<Chip
									label={groupLabel(group)}
									icon={<ArrowDropDown />}
									color={selectedInGroup.length > 0 ? "primary" : "default"}
									variant={selectedInGroup.length > 0 ? "filled" : "outlined"}
									clickable
								/>
							</Box>
						);
					})}
				</Box>

				{openGroupData && (
					<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeGroup}>
						<MenuItem onClick={() => toggleGroup(openGroupData)} dense>
							<Checkbox
								size="small"
								checked={openGroupData.compounds.every((c) => selectedIds.has(c.id))}
								indeterminate={
									openGroupData.compounds.some((c) => selectedIds.has(c.id)) &&
									!openGroupData.compounds.every((c) => selectedIds.has(c.id))
								}
							/>
							<ListItemText primary={`Alle ${openGroupData.company.name} Plätze`} />
						</MenuItem>
						<Divider />
						{openGroupData.compounds.map((compound) => (
							<MenuItem key={compound.id} onClick={() => toggleCompound(compound.id)} dense>
								<Checkbox size="small" checked={selectedIds.has(compound.id)} />
								<ListItemText primary={compound.name} />
							</MenuItem>
						))}
					</Menu>
				)}

				<Box sx={{ display: "flex", gap: "30px", flexWrap: "wrap", flex: "1 0 0px" }}>
					<Box sx={{ minWidth: "270px" }}>
						<CompoundNewOrdersTile compounds={selectedCompounds} />
					</Box>
					<Box sx={{ minWidth: "270px" }}>
						<CompoundMissingPhotosTile compoundIds={selectedCompoundIds} />
					</Box>
					<Box sx={{ minWidth: "270px" }}>
						<CompoundMissingFirstPhotosTile compoundIds={selectedCompoundIds} />
					</Box>
				</Box>

				<Box>
					<Tile title="Fahrzeugeingänge und -ausgänge" subtitle="der letzten zwei Monate">
						<TileContent>
							<CompoundReturnsChart compoundIds={selectedCompoundIds} options={options} />
						</TileContent>
					</Tile>
				</Box>

				<Box>
					<Tile
						title="Haupttätigkeiten"
						subtitle={`Vom ${moment(interval.dateFrom).format("LL")} bis ${moment(interval.dateTo).format("LL")}`}
					>
						<TileContent>
							<CompoundServiceChart interval={interval} compoundIds={selectedCompoundIds} options={options} />
						</TileContent>
						<TileActions>
							<ActionButton variant="text" onClick={handleChangeInterval}>
								Zeitraum
							</ActionButton>
						</TileActions>
					</Tile>
				</Box>
			</Box>
		</Layout>
	);
};
